import apiClient from './index';

export type BulkPreviewRow = {
  rc_name: string;
  rc_address?: string | null;
  building_name: string;
  building_address?: string | null;
  corps_name: string;
  apartment_number: string;
  apartment_suffix?: string | null;
  apartment_floor?: string | null;
  apartment_section?: string | null;
  apartment_area?: string | null;
  apartment_owner?: string | null;
  apartment_heat_room_coefficient?: string | null;
  apartment_heat_calculate_by_area?: string | null;
  device_name?: string | null;
  device_system_type?: string | null;
  device_manufacturer?: string | null;
  device_serial?: string | null;
  device_dev_eui?: string | null;
  device_mqtt_topic?: string | null;
  device_reading_collection_mode?: string | null;
  device_reading_collection_mode_explicit?: boolean;
  device_heat_coefficient?: string | null;
  radiator_model_name?: string | null;
  radiator_manufacturer?: string | null;
  radiator_placement?: string | null;
  radiator_room_label?: string | null;
  radiator_section_count?: string | null;
  radiator_power_watts?: string | null;
  radiator_dimensions_mm?: string | null;
  radiator_height_mm?: string | null;
  radiator_width_mm?: string | null;
  radiator_depth_mm?: string | null;
  radiator_is_corner?: string | null;
  radiator_notes?: string | null;
  pulse_registrar_key?: string | null;
  pulse_registrar_name?: string | null;
  pulse_registrar_manufacturer?: string | null;
  pulse_registrar_model?: string | null;
  pulse_registrar_transport?: string | null;
  pulse_registrar_dev_eui?: string | null;
  pulse_registrar_mqtt_topic?: string | null;
  pulse_registrar_channel_count?: string | null;
  pulse_channel_number?: string | null;
  pulse_weight?: string | null;
  pulse_weight_mode?: string | null;
  pulse_weight_unit?: string | null;
  pulse_baseline_count?: string | null;
  pulse_baseline_meter?: string | null;
  pulse_coefficient_source?: string | null;
  uspd_name?: string | null;
  uspd_line_number?: string | null;
  uspd_transport?: string | null;
  uspd_bus_address?: string | null;
  uspd_profile_key?: string | null;
  radio_head_serial?: string | null;
  radio_head_dev_eui?: string | null;
  radio_head_manufacturer?: string | null;
  radio_head_model?: string | null;
  radio_head_baseline_meter?: string | null;
  device_present?: boolean | null;
  row_index?: number | null;
  row_errors?: string[] | null;
  row_warnings?: string[] | null;
};

export type BulkPreviewResponse = {
  rows: BulkPreviewRow[];
  errors: string[];
  total_rows?: number;
  offset?: number;
  limit?: number;
};

export type BulkUploadResult = {
  rows_processed: number;
  rows_skipped?: number;
  created: {
    residential_complexes: number;
    buildings: number;
    building_corps: number;
    apartments: number;
    devices: number;
    heat_radiators?: number;
    pulse_radio_modules?: number;
    pulse_connections?: number;
    uspd_connections?: number;
    radio_heads?: number;
    radio_head_bindings?: number;
    dev_eui_mappings?: number;
  };
  updated?: {
    residential_complexes?: number;
    buildings?: number;
    building_corps?: number;
    apartments?: number;
    devices?: number;
    dev_eui_mappings?: number;
    heat_radiators?: number;
    pulse_radio_modules?: number;
    pulse_connections?: number;
    uspd_connections?: number;
    radio_heads?: number;
    radio_head_bindings?: number;
  };
};

export const bulkApi = {
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get<Blob>('/bulk/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  async exportObjects(params?: { residential_complex_id?: string; building_id?: string }): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get<Blob>('/bulk/export', {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] || '');
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    let filename = 'billingservice_objects_export.xlsx';
    if (utf8Match?.[1]) {
      try {
        filename = decodeURIComponent(utf8Match[1]);
      } catch {
        filename = utf8Match[1];
      }
    } else if (plainMatch?.[1]) {
      filename = plainMatch[1];
    }
    return { blob: response.data, filename };
  },

  async previewFile(
    file: File,
    { offset = 0, limit = 500, only_errors = false, only_warnings = false }: {
      offset?: number;
      limit?: number;
      only_errors?: boolean;
      only_warnings?: boolean;
    }
  ): Promise<BulkPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    });
    if (only_errors) {
      params.set('only_errors', 'true');
    }
    if (only_warnings) {
      params.set('only_warnings', 'true');
    }

    const response = await apiClient.post<BulkPreviewResponse>(`/bulk/preview?${params.toString()}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async uploadFile(
    file: File,
    {
      skip_invalid_rows = false,
      acknowledge_skip_invalid = false,
    }: {
      skip_invalid_rows?: boolean;
      acknowledge_skip_invalid?: boolean;
    } = {}
  ): Promise<BulkUploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (skip_invalid_rows) {
      params.set('skip_invalid_rows', 'true');
    }
    if (acknowledge_skip_invalid) {
      params.set('acknowledge_skip_invalid', 'true');
    }
    const query = params.toString();
    const response = await apiClient.post<BulkUploadResult>(
      query ? `/bulk/upload?${query}` : '/bulk/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  async importRows(
    rows: BulkPreviewRow[],
    {
      skip_invalid_rows = false,
      acknowledge_skip_invalid = false,
    }: {
      skip_invalid_rows?: boolean;
      acknowledge_skip_invalid?: boolean;
    } = {}
  ): Promise<BulkUploadResult> {
    const response = await apiClient.post<BulkUploadResult>('/bulk/import', {
      rows,
      skip_invalid_rows,
      acknowledge_skip_invalid,
    });
    return response.data;
  },
};
